import { produce } from "immer";
import { GlobalActions, resetAction } from "../../globals/store/actions";
import { createEntityAdapter } from "../../helpers/store/create-entity-adapter";
import { Conversation, Message } from "../interfaces/conversations";
import {
  addInstagramMessageAction,
  SocialChatAction,
  setSelectedInstagramConversationAction,
  setSelectedFacebookConversationAction,
  addFacebookMessageAction,
  setSelectedWhatsappConversationAction,
  addWhatsappMessageAction,
  addWhatsappConversationAction,
} from "./actions";
import {
  SocialChatApiAction,
  getInstagramConversationsFailureAction,
  getInstagramConversationsInitAction,
  getInstagramConversationsSuccessAction,
  getInstagramMessagesFailureAction,
  getInstagramMessagesInitAction,
  getInstagramMessagesSuccessAction,
  postInstagramMessageFailureAction,
  postInstagramMessageInitAction,
  postInstagramMessageSuccessAction,
  postFacebookMessageSuccessAction,
  getFacebookConversationsSuccessAction,
  getFacebookMessagesSuccessAction,
  getFacebookConversationsInitAction,
  getFacebookConversationsFailureAction,
  getFacebookMessagesFailureAction,
  postFacebookMessageInitAction,
  postFacebookMessageFailureAction,
  postFacebookSeenMessageInitAction,
  postFacebookSeenMessageSuccessAction,
  postFacebookSeenMessageFailureAction,
  getWhatsappConversationsSuccessAction,
  getWhatsappMessagesSuccessAction,
  postWhatsappMessageSuccessAction
} from "./api-actions";

export interface SocialChatState {
  instagram: {
    conversations: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
  facebook: {
    conversations: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
  whatsapp: {
    conversations: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  }
}

const initialState: SocialChatState = {
  instagram: {
    conversations: {
      ids: [],
      entities: {},
    },
    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
  facebook: {
    conversations: {
      ids: [],
      entities: {},
    },
    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
  whatsapp: {
    conversations: {
      ids: [],
      entities: {},
    },
    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  }
};

const conversationsAdapter = createEntityAdapter<Conversation>(
  (payload) => payload.id
);
const messagesAdapter = createEntityAdapter<Message>(
  (payload) => payload.id
);

export default produce(
  (
    draft: SocialChatState = initialState,
    action: SocialChatAction | SocialChatApiAction | GlobalActions
  ) => {
    switch (action.type) {
      case setSelectedInstagramConversationAction.type:
        if (draft.instagram.selected?.id === action.id || !action.id) {
          draft.instagram.selected = null;
        } else {
          const conv1 = draft.instagram.conversations.entities[action.id];
          draft.instagram.selected = conv1;

          conversationsAdapter.updateOne(
            { ...conv1, unreadCount: 0 },
            draft.instagram.conversations
          );
        }
        return draft;
      case addInstagramMessageAction.type:
        let conv2 = draft.instagram.conversations.ids
          .map((id) => draft.instagram.conversations.entities[id])
          .find((conv) => conv.participants.some(
            (participant) => participant.id === action.id
          ));

        if (conv2) {
          if (draft.instagram.selected?.id === `${conv2.id}`) {
            messagesAdapter.setOne(action.message, draft.instagram.messages);
          } else {
            conv2 = { ...conv2, unreadCount: conv2.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv2,
              snippet: action.message.message,
              timestamp: action.message.timestamp,
            },
            draft.instagram.conversations
          );
          conversationsAdapter.moveToFirstPosition(conv2.id, draft.instagram.conversations);
        }

        return draft;
      case postInstagramMessageSuccessAction.type:
        let conv3 = draft.instagram.conversations.entities[action.id];

        messagesAdapter.setOne(action.message, draft.instagram.messages);

        conversationsAdapter.updateOne(
          { ...conv3, snippet: action.message.message },
          draft.instagram.conversations
        );
        conversationsAdapter.moveToFirstPosition(action.id, draft.instagram.conversations);
        return draft;
      case getInstagramConversationsSuccessAction.type:
        conversationsAdapter.addMany(action.conversations, draft.instagram.conversations);
        return draft;
      case getInstagramMessagesSuccessAction.type:
        messagesAdapter.addMany(action.messages, draft.instagram.messages);
        return draft;
      case getInstagramMessagesInitAction.type:
        messagesAdapter.removeAll(draft.instagram.messages);
        return draft;
      case setSelectedFacebookConversationAction.type:
        if (draft.facebook.selected?.fromId === action.id || !action.id) {
          draft.facebook.selected = null;
        } else {
          const conv4 = draft.facebook.conversations.entities[action.id];
          draft.facebook.selected = conv4;

          conversationsAdapter.updateOne(
            { ...conv4, unreadCount: 0 },
            draft.facebook.conversations
          );
        }
        return draft;
      case addFacebookMessageAction.type:
        let conv5 = draft.facebook.conversations.ids
          .map((id) => draft.facebook.conversations.entities[id])
          .find((conv) => conv.participants.some(
            (participant) => participant.id === action.id
          ));

        if (conv5) {
          if (draft.facebook.selected?.id === `${conv5.id}`) {
            messagesAdapter.setOne(action.message, draft.facebook.messages);
          } else {
            conv5 = { ...conv5, unreadCount: conv5.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv5,
              snippet: action.message.message,
              timestamp: action.message.timestamp,
            },
            draft.facebook.conversations
          );
          conversationsAdapter.moveToFirstPosition(conv5.id, draft.facebook.conversations);
        }

        return draft;
      case postFacebookMessageSuccessAction.type:
        let conv6 = draft.facebook.conversations.entities[action.id];

        messagesAdapter.setOne(action.message, draft.facebook.messages);

        conversationsAdapter.updateOne(
          { ...conv6, snippet: action.message.message },
          draft.facebook.conversations
        );
        conversationsAdapter.moveToFirstPosition(action.id, draft.facebook.conversations);
        return draft;
      case getFacebookConversationsSuccessAction.type:
        conversationsAdapter.addMany(action.conversations, draft.facebook.conversations);
        return draft;
      case getFacebookMessagesSuccessAction.type:
        messagesAdapter.addMany(action.messages, draft.facebook.messages);
        return draft;
      case setSelectedWhatsappConversationAction.type:
        if (draft.whatsapp.selected?.id === action.id || !action.id) {
          draft.whatsapp.selected = null;
        } else {
          const conv9 = draft.whatsapp.conversations.entities[action.id];
          draft.whatsapp.selected = conv9;

          conversationsAdapter.updateOne(
            { ...conv9, unreadCount: 0 },
            draft.whatsapp.conversations
          );
        }
        return draft;
      case addWhatsappMessageAction.type:
        let conv7 = draft.whatsapp.conversations.entities[action.id];

        if (conv7) {
          if (draft.whatsapp.selected?.id === conv7.id) {
            messagesAdapter.setOne(action.message, draft.whatsapp.messages);
          } else {
            conv7 = { ...conv7, unreadCount: conv7.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv7,
              snippet: action.message.message,
              timestamp: action.message.timestamp,
            },
            draft.whatsapp.conversations
          );
          conversationsAdapter.moveToFirstPosition(conv7.id, draft.whatsapp.conversations);
        }

        return draft;
      case addWhatsappConversationAction.type:
        conversationsAdapter.setOne(action.conversation, draft.whatsapp.conversations);
        return draft;
      case postWhatsappMessageSuccessAction.type:
        let conv8 = draft.whatsapp.conversations.entities[+action.id];

        messagesAdapter.setOne(action.message, draft.whatsapp.messages);

        conversationsAdapter.updateOne(
          { ...conv8, snippet: action.message.message },
          draft.whatsapp.conversations
        );
        conversationsAdapter.moveToFirstPosition(+action.id, draft.whatsapp.conversations);
        return draft;
      case getWhatsappConversationsSuccessAction.type:
        conversationsAdapter.addMany(action.conversations, draft.whatsapp.conversations);
        return draft;
      case getWhatsappMessagesSuccessAction.type:
        messagesAdapter.addMany(action.messages, draft.whatsapp.messages);
        return draft;
      case resetAction.type:
        return initialState;
      case getInstagramConversationsInitAction.type:
      case getInstagramConversationsFailureAction.type:
      case getInstagramMessagesFailureAction.type:
      case postInstagramMessageInitAction.type:
      case postInstagramMessageFailureAction.type:
      case getFacebookConversationsInitAction.type:
      case getFacebookConversationsFailureAction.type:
      case getFacebookMessagesFailureAction.type:
      case postFacebookMessageInitAction.type:
      case postFacebookMessageFailureAction.type:
      case postFacebookSeenMessageInitAction.type:
      case postFacebookSeenMessageSuccessAction.type:
      case postFacebookSeenMessageFailureAction.type:
      default:
        return draft;
    }
  }
);
